import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, TextField,
  MenuItem, Grid, Card, CardContent, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  WarningAmber as WarnIcon,
  Refresh as RefreshIcon,
  DeleteSweep as PurgeIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface LogEntry {
  id: number;
  site_id: number | null;
  level: string;
  method: string | null;
  route: string | null;
  status_code: number | null;
  message: string;
  stack_trace: string | null;
  user_id: number | null;
  created_at: string;
}

interface Stats {
  by_level: Record<string, number>;
  total: number;
  top_errors_24h: { route: string; status_code: number; count: number }[];
}

const levelColor = (l: string): 'error' | 'warning' | 'info' | 'default' =>
  l === 'error' ? 'error' : l === 'warn' ? 'warning' : l === 'info' ? 'info' : 'default';

const statusColor = (code: number | null) => {
  if (!code) return 'default';
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  return 'success';
};

const LogsPage = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [levelFilter, setLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Stack trace drawer
  const [traceEntry, setTraceEntry] = useState<LogEntry | null>(null);

  // Purge dialog
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState('90');
  const [purging, setPurging] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (levelFilter)  params.set('level', levelFilter);
      if (statusFilter) params.set('status_code', statusFilter);
      if (routeFilter)  params.set('route', routeFilter);
      if (dateFrom)     params.set('date_from', dateFrom);
      if (dateTo)       params.set('date_to', dateTo);

      const [logsRes, statsRes] = await Promise.all([
        api.get<{ data: LogEntry[]; total: number }>(`${API_ENDPOINTS.LOGS.LIST}?${params}`),
        api.get<Stats>(API_ENDPOINTS.LOGS.STATS),
      ]);
      setLogs(logsRes.data ?? []);
      setTotal(logsRes.total ?? 0);
      setStats(statsRes);
    } catch {
      setError('Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [levelFilter, statusFilter, routeFilter, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePurge = async () => {
    setPurging(true);
    setPurgeMsg('');
    try {
      const res = await api.delete<{ deleted: number }>(`${API_ENDPOINTS.LOGS.PURGE}?days=${purgeDays}`);
      setPurgeMsg(`Deleted ${res.deleted} log entries older than ${purgeDays} days.`);
      fetchData();
    } catch {
      setPurgeMsg('Purge failed.');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon color="error" />
          <Typography variant="h5" fontWeight={700}>Error Logs</Typography>
          <Chip label={`${total} total`} size="small" />
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<RefreshIcon />} onClick={fetchData} size="small">Refresh</Button>
          <Button startIcon={<PurgeIcon />} color="error" onClick={() => setPurgeOpen(true)} size="small">
            Purge Old Logs
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Errors', value: stats.by_level.error ?? 0, icon: <ErrorIcon />, color: 'error.main' },
            { label: 'Warnings', value: stats.by_level.warn ?? 0, icon: <WarnIcon />, color: 'warning.main' },
            { label: 'Info', value: stats.by_level.info ?? 0, icon: <ErrorIcon />, color: 'info.main' },
            { label: 'Total', value: stats.total, icon: <ErrorIcon />, color: 'text.primary' },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {stats.top_errors_24h.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Top errors (last 24h)
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {stats.top_errors_24h.map((e, i) => (
                      <Chip key={i} size="small" label={`${e.route} ${e.status_code} ×${e.count}`}
                        color={e.status_code >= 500 ? 'error' : 'warning'} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={2}>
            <TextField select fullWidth size="small" label="Level" value={levelFilter}
              onChange={e => setLevelFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" label="Status code" value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)} placeholder="e.g. 404" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Route contains" value={routeFilter}
              onChange={e => setRouteFilter(e.target.value)} placeholder="/api/v1/..." />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button fullWidth size="small" onClick={fetchData} variant="contained">Filter</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Log table */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Message</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No log entries found</TableCell>
                </TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} hover
                  sx={{ bgcolor: log.level === 'error' ? 'error.50' : log.level === 'warn' ? 'warning.50' : undefined }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{log.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {log.created_at.replace('T', ' ').slice(0, 19)}
                  </TableCell>
                  <TableCell>
                    <Chip label={log.level} size="small" color={levelColor(log.level)} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{log.method ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 200 }}>
                    <Tooltip title={log.route ?? ''}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                        {log.route ?? '—'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {log.status_code
                      ? <Chip label={log.status_code} size="small" color={statusColor(log.status_code) as 'error' | 'warning' | 'success' | 'default'} />
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Tooltip title={log.message}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: 12 }}>
                        {log.message}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {log.stack_trace && (
                      <Button size="small" onClick={() => setTraceEntry(log)}>Trace</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Stack trace dialog */}
      <Dialog open={!!traceEntry} onClose={() => setTraceEntry(null)} maxWidth="md" fullWidth>
        <DialogTitle>Stack Trace — Log #{traceEntry?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {traceEntry?.route} · {traceEntry?.created_at}
          </Typography>
          <Box component="pre" sx={{
            bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1,
            fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {traceEntry?.stack_trace}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTraceEntry(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Purge dialog */}
      <Dialog open={purgeOpen} onClose={() => setPurgeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Purge Old Logs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Delete all log entries older than the specified number of days.
          </Typography>
          <TextField fullWidth size="small" label="Older than (days)" type="number"
            value={purgeDays} onChange={e => setPurgeDays(e.target.value)} />
          {purgeMsg && <Alert severity="info" sx={{ mt: 1 }}>{purgeMsg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handlePurge} disabled={purging}>
            {purging ? <CircularProgress size={20} /> : 'Purge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogsPage;
